import { Link as LinkIcon } from "@phosphor-icons/react"
import { useQuery } from "@tanstack/react-query"
import { Heading } from "@tokenterminal/core/ui/heading/Heading"
import { Icon } from "@tokenterminal/core/ui/icon/Icon"
import { SearchField } from "@tokenterminal/core/ui/input/SearchField"
import {
  ToggleButton,
  ToggleButtonGroup,
} from "@tokenterminal/core/ui/input/toggle/ToggleButtonGroup"
import { Card } from "@tokenterminal/core/ui/layout/card/Card"
import { Divider } from "@tokenterminal/core/ui/layout/divider/Divider"
import {
  Badge,
  type BadgeProps,
} from "@tokenterminal/core/ui/messaging/badge/Badge"
import { EmptyState } from "@tokenterminal/core/ui/messaging/emptyState/EmptyState"
import { Tooltip } from "@tokenterminal/core/ui/overlays/tooltip/Tooltip"
import { formatNumber } from "@tokenterminal/core/utils/sharedFormatter"
import useProjects from "hooks/trpc/useProjects"
import { useMetricSorting } from "hooks/useMetricSorting"
import { useAvailableEcosystemMetricIds } from "modules/projects/hooks/useAvailableEcosystemMetricIds"
import { type MetricConfiguration } from "modules/projects/hooks/useMetricsConfigurationList"
import Link from "next/link"
import { useMemo, useState, useRef, useEffect, useCallback } from "react"
import { getMetricSortingConfig } from "utils/metricSortingConfig"
import { type RouterOutputs, useTRPC } from "utils/trpc"
import SidebarCategoryList from "./SidebarCategoryList"

const capitalizeFirstLetter = (str: string) => {
  return str.charAt(0).toUpperCase() + str.slice(1)
}

export const EcosystemBadge = ({
  children = "Ecosystem",
  ...props
}: Omit<BadgeProps, "variant">) => {
  return (
    <Tooltip
      tooltipProps={{
        className: "rounded-lg",
      }}
      content={
        <div className="flex flex-col gap-1 p-1 rounded-lg">
          Ecosystem metrics are metrics that are used to track the performance
          of all projects on a blockchain.
        </div>
      }
    >
      <Tooltip.Trigger>
        <Badge variant="muted" {...props}>
          {children}
        </Badge>
      </Tooltip.Trigger>
    </Tooltip>
  )
}

const MetricListItem = ({
  metric,
  isEcosystem,
}: {
  metric: RouterOutputs["metrics"]["getMetricsConfiguration"]["data"][number]
  isEcosystem: boolean
}) => {
  const { data: { data: projects = [] } = {}, isLoading } = useProjects({
    includeMetrics: true,
  })

  const projectSlugs = useMemo(() => {
    const projectSlugs = projects
      ?.filter((project) =>
        project.available_metrics?.includes(metric.metric_id)
      )
      .map((project) => project.slug)

    // Ensure no duplicates - was a problem where aethir was showing up twice
    return Array.from(new Set(projectSlugs))
  }, [projects, metric])

  return (
    <Link
      href={`/metrics/${metric.slug}`}
      key={metric.slug}
      className="group flex justify-between items-center w-[calc(100%+theme(spacing.4)+theme(spacing.4))] gap-12 py-3 px-4 -my-3 -mx-4 hover:bg-neutral-950/5 dark:hover:bg-neutral-50/5 focus:bg-neutral-950/5 dark:focus:bg-neutral-50/5 rounded-lg"
    >
      <div>
        <div className="flex items-center gap-1">
          <h5 className="text-base font-medium">{metric.title}</h5>
          {isEcosystem && <EcosystemBadge size="small" />}
        </div>
        <p className="text-sm text-fg-secondary w-full text-pretty">
          {capitalizeFirstLetter(metric.definition ?? "")}
        </p>
      </div>
      <div
        className="text-sm text-fg-secondary hidden sm:block"
        style={{
          opacity: isLoading ? 0 : 1,
          transition: "opacity 0.2s ease-in-out",
        }}
      >
        {formatNumber({ value: projectSlugs.length, format: "full" })}
      </div>
    </Link>
  )
}

const MetricsListPage = () => {
  const trpc = useTRPC()
  const [search, setSearch] = useState("")
  const [isAlphabetical, setIsAlphabetical] = useState(false)
  const {
    data: { data: allMetricsConfigurations = [] } = {},
    isLoading: isMetricsLoading,
  } = useQuery(trpc.metrics.getMetricsConfiguration.queryOptions())
  const availableMetrics = useMemo(
    () => allMetricsConfigurations.filter((metric) => metric.dashboard_enabled),
    [allMetricsConfigurations]
  )
  const {
    data: ecosystemMetricIds = [],
    isLoading: isLoadingEcosystemMetricIds,
  } = useAvailableEcosystemMetricIds()
  const isLoading = isMetricsLoading || isLoadingEcosystemMetricIds

  // Split metrics into ecosystem and non-ecosystem
  const ecosystemMetrics = useMemo(
    () =>
      availableMetrics.filter(
        (metric) =>
          ecosystemMetricIds.includes(metric.metric_id) &&
          (metric.title.toLowerCase().includes(search.toLowerCase()) ||
            metric.metric_id.toLowerCase().includes(search.toLowerCase()))
      ),
    [availableMetrics, ecosystemMetricIds, search]
  )
  const nonEcosystemMetrics = useMemo(
    () => availableMetrics.filter((metric) => !metric.is_ecosystem_metric),
    [availableMetrics]
  )

  // Create a map for metric lookup (moved outside the memo)
  const metricConfigMap = useMemo(() => {
    const map = new Map<string, MetricConfiguration>()
    nonEcosystemMetrics.forEach((metric) => {
      map.set(metric.metric_id, metric)
    })
    return map
  }, [nonEcosystemMetrics])

  // Group metrics by category (moved outside the memo)
  const categorizedMetrics = useMemo(() => {
    const categories: Record<string, MetricConfiguration[]> = {}
    nonEcosystemMetrics.forEach((metric: MetricConfiguration) => {
      if (metric.categories && metric.categories.length > 0) {
        const category = metric.categories[0]
        if (!categories[category]) {
          categories[category] = []
        }
        categories[category].push(metric)
      }
    })
    return categories
  }, [nonEcosystemMetrics])

  const totalMetricCount = useMemo(() => {
    return availableMetrics.length
  }, [availableMetrics])

  // Get the sorting results using the hook
  const sortingConfig = useMemo(() => getMetricSortingConfig(), [])
  const { sortedMetricsByCategory, sortedCategories } = useMetricSorting(
    categorizedMetrics,
    metricConfigMap,
    sortingConfig
  )

  // Grouped metrics for rendering
  const groupedMetrics = useMemo(() => {
    if (isAlphabetical) {
      // Group all metrics alphabetically (including ecosystem)
      const alphabetGroups: Record<string, MetricConfiguration[]> = {}
      availableMetrics
        .filter(
          (metric) =>
            metric.title.toLowerCase().includes(search.toLowerCase()) ||
            metric.metric_id.toLowerCase().includes(search.toLowerCase())
        )
        .forEach((metric: MetricConfiguration) => {
          const firstLetter = metric.title.charAt(0).toUpperCase()
          if (!alphabetGroups[firstLetter]) {
            alphabetGroups[firstLetter] = []
          }
          alphabetGroups[firstLetter].push(metric)
        })
      return Object.entries(alphabetGroups)
        .map(([letter, metrics]) => ({
          slug: letter.toLowerCase(),
          title: letter,
          categories: [],
          children: metrics.sort((a, b) => a.title.localeCompare(b.title)),
        }))
        .filter((group) => group.children.length > 0)
        .sort((a, b) => a.title.localeCompare(b.title))
    } else {
      // Only non-ecosystem metrics for categorical
      return sortedCategories
        .map((category: string) => ({
          slug: category.toLowerCase().replace(/\s+/g, "-"),
          title: category,
          categories: [],
          children: sortedMetricsByCategory[category].filter(
            (metric: MetricConfiguration) =>
              metric.title.toLowerCase().includes(search.toLowerCase()) ||
              metric.metric_id.toLowerCase().includes(search.toLowerCase())
          ),
        }))
        .filter((group) => group.children.length > 0)
    }
  }, [
    availableMetrics,
    search,
    isAlphabetical,
    sortedCategories,
    sortedMetricsByCategory,
  ])

  // Sidebar/jump list categories (Ecosystem last if present, only in Categorical mode)
  const categoryNames = useMemo(() => {
    if (isAlphabetical) {
      return groupedMetrics.map((group) => group.title)
    }
    const names = groupedMetrics.map((group) => group.title)
    return ecosystemMetrics.length > 0 ? [...names, "Ecosystem"] : names
  }, [groupedMetrics, ecosystemMetrics.length, isAlphabetical])

  const linkRefs = useRef<HTMLAnchorElement[]>([])

  const scrollToAnchorByHref = useCallback((href: string) => {
    const anchor = document.querySelector(href)
    if (anchor) {
      const anchorTop = anchor.getBoundingClientRect().top + window.scrollY
      setTimeout(() => {
        document.documentElement.scrollTo({
          top: anchorTop,
          behavior: "smooth",
        })
      }, 100)
    }
  }, [])

  const handleClickAnchorLink = useCallback(
    (e: MouseEvent) => {
      e.preventDefault()
      const target = e.target as HTMLElement

      const closestAnchor = target.closest("a")
      if (closestAnchor) {
        const href = closestAnchor.getAttribute("href")

        if (href?.startsWith("#")) {
          e.preventDefault()
          scrollToAnchorByHref(href)

          window.history.pushState(null, "", href)
        }
      }
    },
    [scrollToAnchorByHref]
  )

  useEffect(() => {
    const links = linkRefs.current
    // Wait until we're done loading to add event listeners
    if (!isLoading) {
      links.forEach((link) => {
        link.addEventListener("click", handleClickAnchorLink)
      })
    }

    return () => {
      links.forEach((link) => {
        link.removeEventListener("click", handleClickAnchorLink)
      })
    }
    // Re-establish event listeners if the user changes isAlphabetical
  }, [handleClickAnchorLink, isLoading, isAlphabetical])

  useEffect(() => {
    const hash = window.location.hash

    if (hash.replace("#", "").length === 1) {
      setIsAlphabetical(true)
    } else if (hash.replace("#", "").length > 1) {
      setIsAlphabetical(false)
    }

    if (hash && !isLoading) {
      scrollToAnchorByHref(hash)
    }
  }, [scrollToAnchorByHref, isLoading])

  return (
    <div className="relative mx-auto max-w-full">
      <div className="grid grid-cols-1 lg:grid-cols-[110px_800px_110px] xl:grid-cols-[150px_1000px_150px] gap-x-[50px] xl:gap-x-[100px] gap-y-4 px-4 mt-10">
        {/* Top row - Header in center, empty left and right */}
        <div className="hidden lg:block col-start-1 col-end-2"></div>
        <div className="lg:col-start-2 lg:col-end-3">
          <Heading level={1}>Metrics</Heading>
          <p className="text-sm text-fg-secondary mb-6">
            Explore our{" "}
            <span
              style={{
                opacity: isLoading ? 0 : 1,
                transition: "opacity 0.2s ease-in-out",
              }}
            >
              {/* Using 99 as a placeholder since it's 2 digits and matches our current count */}
              {isLoading ? 99 : totalMetricCount}
            </span>{" "}
            metrics and definitions
          </p>

          <div className="flex flex-col gap-4 mb-8">
            <div className="flex flex-col min-[550px]:flex-row gap-2">
              <SearchField
                placeholder="Filter"
                aria-label="Filter"
                value={search}
                onChange={setSearch}
                className="flex-1"
              />
              <ToggleButtonGroup
                selectedKey={isAlphabetical ? "alphabetical" : "categorical"}
                onSelectionChange={(key) => {
                  setIsAlphabetical(key === "alphabetical")
                  window.history.pushState(null, "", "metrics")
                }}
                className="max-[550px]:w-full"
              >
                <ToggleButton id="categorical" className="max-[550px]:w-full">
                  Categorical
                </ToggleButton>
                <ToggleButton id="alphabetical" className="max-[550px]:w-full">
                  Alphabetical
                </ToggleButton>
              </ToggleButtonGroup>
            </div>
          </div>
        </div>
        <div className="hidden lg:block lg:col-start-3 lg:col-end-4"></div>

        {/* Second row - Jump to on left, content in center, empty right */}
        <div className="hidden lg:block lg:col-start-1 lg:col-end-2 sticky top-0 self-start">
          <div
            className={`${groupedMetrics.length + (ecosystemMetrics.length > 0 ? 1 : 0) <= 1 ? "hidden" : "flex flex-col gap-1 items-end mt-[50px]"}`}
          >
            <label className="block text-sm text-fg-secondary mb-2 opacity-80 mr-1.5">
              Jump to
            </label>
            <SidebarCategoryList
              categories={categoryNames}
              linkRefs={linkRefs}
              counts={[
                ...groupedMetrics.map((group) => group.children.length),
                ...(ecosystemMetrics.length > 0
                  ? [ecosystemMetrics.length]
                  : []),
              ]}
            />
          </div>
        </div>

        <div className="lg:col-start-2 lg:col-end-3 flex flex-col gap-8">
          {/* Main content: Alphabetical mode shows all metrics together, Categorical mode splits out Ecosystem */}
          {isAlphabetical ? (
            groupedMetrics.length > 0 ? (
              groupedMetrics.map((group) => (
                <div className="mb-8 relative" key={group.slug}>
                  <div
                    id={group.slug}
                    className="absolute top-[-20px] left-0"
                  />
                  <div className="flex justify-between items-center">
                    <a
                      className="inline-flex items-center gap-1 group"
                      href={`#${group.slug}`}
                      // @ts-ignore - Event listener type mismatch
                      onClick={handleClickAnchorLink}
                    >
                      <Icon
                        as={LinkIcon}
                        width={16}
                        className="text-fg-secondary opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100 ml-[calc(-16px-theme(spacing.1))] transition-opacity duration-100"
                      />
                      <Heading level={3}>
                        {capitalizeFirstLetter(group.title)}
                        <span className="text-fg-secondary font-normal ml-1.5">
                          ({group.children.length})
                        </span>
                      </Heading>
                    </a>
                    <div className="text-sm text-fg-secondary whitespace-nowrap hidden sm:flex items-center gap-1">
                      Projects
                    </div>
                  </div>
                  <Divider className="my-6" />
                  <div className="flex flex-col gap-8">
                    {group.children.map((child) => (
                      <MetricListItem
                        key={child.slug}
                        metric={child}
                        isEcosystem={ecosystemMetricIds.includes(
                          child.metric_id
                        )}
                      />
                    ))}
                  </div>
                </div>
              ))
            ) : (
              !isLoading && (
                <Card className="rounded-xl bg-transparent">
                  <EmptyState
                    headingText="No metrics found"
                    bodyText="Try a different search or filter."
                  />
                </Card>
              )
            )
          ) : (
            <>
              {/* Categorical mode: grouped metrics, then Ecosystem section at the bottom */}
              {groupedMetrics.length > 0 &&
                groupedMetrics.map((group) => (
                  <div className="mb-8 relative" key={group.slug}>
                    <div
                      id={group.slug}
                      className="absolute top-[-20px] left-0"
                    />
                    <div className="flex justify-between items-center">
                      <a
                        className="inline-flex items-center gap-1 group"
                        href={`#${group.slug}`}
                        // @ts-ignore - Event listener type mismatch
                        onClick={handleClickAnchorLink}
                      >
                        <Icon
                          as={LinkIcon}
                          width={16}
                          className="text-fg-secondary opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100 ml-[calc(-16px-theme(spacing.1))] transition-opacity duration-100"
                        />
                        <Heading level={3}>
                          {capitalizeFirstLetter(group.title)}
                          <span className="text-fg-secondary font-normal ml-1.5">
                            ({group.children.length})
                          </span>
                        </Heading>
                      </a>
                      <div className="text-sm text-fg-secondary whitespace-nowrap hidden sm:flex items-center gap-1">
                        Projects
                      </div>
                    </div>
                    <Divider className="my-6" />
                    <div className="flex flex-col gap-8">
                      {group.children.map((child) => (
                        <MetricListItem
                          key={child.slug}
                          metric={child}
                          isEcosystem={false}
                        />
                      ))}
                    </div>
                  </div>
                ))}
              {/* Ecosystem section at the bottom */}
              {ecosystemMetrics.length > 0 && (
                <div className="mb-8 relative" key="ecosystem">
                  <div id="ecosystem" className="absolute top-[-20px] left-0" />
                  <div className="flex justify-between items-center">
                    <a
                      className="inline-flex items-center gap-1 group"
                      href="#ecosystem"
                      // @ts-ignore - Event listener type mismatch
                      onClick={handleClickAnchorLink}
                    >
                      <Icon
                        as={LinkIcon}
                        width={16}
                        className="text-fg-secondary opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100 ml-[calc(-16px-theme(spacing.1))] transition-opacity duration-100"
                      />
                      <Heading level={3}>
                        Ecosystem
                        <span className="text-fg-secondary font-normal ml-1.5">
                          ({ecosystemMetrics.length})
                        </span>
                      </Heading>
                    </a>
                    <div className="text-sm text-fg-secondary whitespace-nowrap hidden sm:flex items-center gap-1">
                      Projects
                    </div>
                  </div>
                  <Divider className="my-6" />
                  <div className="flex flex-col gap-8">
                    {ecosystemMetrics.map((metric) => (
                      <MetricListItem
                        key={metric.slug}
                        metric={metric}
                        isEcosystem={true}
                      />
                    ))}
                  </div>
                </div>
              )}
              {groupedMetrics.length === 0 &&
                ecosystemMetrics.length === 0 &&
                !isLoading && (
                  <Card className="rounded-xl bg-transparent">
                    <EmptyState
                      headingText="No metrics found"
                      bodyText="Try a different search or filter."
                    />
                  </Card>
                )}
            </>
          )}
          {/* Placeholder to avoid footer jumping when loading */}
          {isLoading && <div className="h-[2000px]" />}
        </div>

        <div className="hidden lg:block lg:col-start-3 lg:col-end-4"></div>
      </div>
    </div>
  )
}

export default MetricsListPage
